/**
 * HorizontalScroll — horizontal card row, as in our trackers (support/tech/des HorizontalScroll.tsx):
 * an arrow and a fade appear only on the side that can still be scrolled. The vertical mouse wheel scrolls
 * the row sideways; at the edge the row just stops and the page under it does not scroll.
 * Extra for trends: the row can be dragged with the mouse (as the home rows could before).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from './ui/button';

// Movement after which a press counts as a drag, not a click on a card
const DRAG_THRESHOLD_PX = 5;

const HorizontalScroll = ({ children, contentClassName = '' }) => {
    const scrollerRef = useRef(null);
    const [edges, setEdges] = useState({ left: false, right: false });
    // Where the smooth wheel scroll is heading: the next wheel notch adds to the target, not to the current
    // position, otherwise fast scrolling loses distance.
    const wheelTarget = useRef(0);
    const lastWheelAt = useRef(0);
    const drag = useRef({ active: false, moved: false, startX: 0, startLeft: 0 });

    const updateEdges = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        const next = { left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 };
        setEdges((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
    }, []);

    const scrollToLeft = (left) => {
        const el = scrollerRef.current;
        if (!el) return;
        wheelTarget.current = Math.max(0, Math.min(el.scrollWidth - el.clientWidth, left));
        el.scrollTo({ left: wheelTarget.current, behavior: 'smooth' });
    };

    const scrollByPage = (direction) => {
        const el = scrollerRef.current;
        if (!el) return;
        lastWheelAt.current = 0;
        scrollToLeft(el.scrollLeft + direction * el.clientWidth * 0.8);
    };

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return undefined;

        // Edges are recalculated on scroll, on resize and when cards change
        // (the row width stays the same then, hence the MutationObserver too).
        const resizeObserver = new ResizeObserver(updateEdges);
        resizeObserver.observe(el);
        const mutationObserver = new MutationObserver(updateEdges);
        mutationObserver.observe(el, { childList: true, subtree: true });
        el.addEventListener('scroll', updateEdges, { passive: true });

        const onWheel = (e) => {
            // A horizontal touchpad gesture and Shift+wheel are scrolled by the browser itself.
            if (e.ctrlKey || Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
            const max = el.scrollWidth - el.clientWidth;
            // All cards fit - nothing to scroll, the wheel stays with the page.
            if (max <= 0) return;
            // Otherwise the wheel over the row scrolls only the row, at the edge too.
            e.preventDefault();
            const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientWidth : 1;
            const now = performance.now();
            const base = now - lastWheelAt.current < 250 ? wheelTarget.current : el.scrollLeft;
            const next = Math.max(0, Math.min(max, base + e.deltaY * unit));
            lastWheelAt.current = now;
            wheelTarget.current = next;
            if (next !== el.scrollLeft) el.scrollTo({ left: next, behavior: 'smooth' });
        };
        // passive: false - otherwise preventDefault does not stop the page scroll.
        el.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            el.removeEventListener('scroll', updateEdges);
            el.removeEventListener('wheel', onWheel);
        };
    }, [updateEdges]);

    // Mouse drag: the row follows the pointer; a real drag swallows the click so no card opens.
    const onMouseDown = (e) => {
        const el = scrollerRef.current;
        if (!el || e.button !== 0) return;
        drag.current = { active: true, moved: false, startX: e.pageX, startLeft: el.scrollLeft };
    };
    const onMouseMove = (e) => {
        const el = scrollerRef.current;
        if (!el || !drag.current.active) return;
        const dx = e.pageX - drag.current.startX;
        if (!drag.current.moved && Math.abs(dx) < DRAG_THRESHOLD_PX) return;
        if (!drag.current.moved) {
            drag.current.moved = true;
            el.style.cursor = 'grabbing';
            el.style.userSelect = 'none';
        }
        e.preventDefault();
        el.scrollLeft = drag.current.startLeft - dx;
    };
    const endDrag = () => {
        const el = scrollerRef.current;
        drag.current.active = false;
        if (el) {
            el.style.removeProperty('cursor');
            el.style.removeProperty('user-select');
        }
    };
    const onClickCapture = (e) => {
        if (drag.current.moved) {
            e.preventDefault();
            e.stopPropagation();
            drag.current.moved = false;
        }
    };

    return (
        <div className="relative">
            {edges.left && (
                <>
                    <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-16 bg-gradient-to-r from-background to-transparent" />
                    <Button
                        variant="outline"
                        size="icon"
                        aria-label="Прокрутить влево"
                        onClick={() => scrollByPage(-1)}
                        className="absolute left-1 top-1/2 z-20 h-8 w-8 -translate-y-1/2 rounded-full bg-background/90 shadow-md backdrop-blur"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                </>
            )}
            {edges.right && (
                <>
                    <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-16 bg-gradient-to-l from-background to-transparent" />
                    <Button
                        variant="outline"
                        size="icon"
                        aria-label="Прокрутить вправо"
                        onClick={() => scrollByPage(1)}
                        className="absolute right-1 top-1/2 z-20 h-8 w-8 -translate-y-1/2 rounded-full bg-background/90 shadow-md backdrop-blur"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </>
            )}
            <div
                ref={scrollerRef}
                className="overflow-x-auto overscroll-x-contain hide-scrollbar cursor-grab"
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                onClickCapture={onClickCapture}
            >
                <div className={contentClassName}>{children}</div>
            </div>
        </div>
    );
};

export default HorizontalScroll;
